import { DashboardApi } from './dashboardApi';
import moment from 'moment';

function buildEmptyStats() {
    return { allTimeTotal: null, last7DaysTotal: null, lastCreateTime: null };
}

function formatStats(stats) {
    return {
        allTimeTotal: stats.allTimeTotal,
        last7DaysTotal: stats.last7DaysTotal,
        lastCreateTime: stats.lastCreateTime
    };
}

function formatData(data) {
    return {
        users: formatStats(data.users),
        equipments: formatStats(data.equipments),
        inspections: formatStats(data.inspections)
    };
}

function summary(filterCompanyId) {
    return DashboardApi.summary(filterCompanyId).then(response => formatData(response.data));
}

function monthlyInspections(filterCompanyId) {
    return DashboardApi.monthlyInspections(filterCompanyId).then(response => response.data);
}

function equipmentStatus(filterCompanyId) {
    return DashboardApi.equipmentStatus(filterCompanyId).then(response => response.data);
}

function employeeInspections(dateStart, dateEnd, filterCompanyId) {
    return DashboardApi.employeeInspections(dateStart, dateEnd, filterCompanyId).then(response => response.data);
}

function inspectionTypeInspections(filterCompanyId) {
    return DashboardApi.inspectionTypeInspections(filterCompanyId).then(response => response.data);
}

function productInspections(dateStart, dateEnd, filterCompanyId) {
    return DashboardApi.productInspections(dateStart, dateEnd, filterCompanyId).then(response => response.data);
}

/**
 * Process product inspections data to calculate conformity rate trend
 * @param {Array} productData - Raw product inspections data with name, answer, qtt fields
 * @returns {Object} { labels: string[], conformityRates: number[], nonConformityRates: number[] }
 */
function processConformityTrend(productData) {
    if (!productData || productData.length === 0) {
        return { labels: [], conformityRates: [], nonConformityRates: [] };
    }

    // Group by month/year if available, otherwise by product
    const monthlyData = {};

    productData.forEach(item => {
        const key = item.yearMonth || item.name || 'unknown';
        if (!monthlyData[key]) {
            monthlyData[key] = { valid: 0, invalid: 0, total: 0 };
        }

        const qty = item.qtt || 0;
        if (item.answer === 'YES' || item.answer === 'NA') {
            monthlyData[key].valid += qty;
        } else if (item.answer === 'NO') {
            monthlyData[key].invalid += qty;
        }
        monthlyData[key].total += qty;
    });

    const sortedKeys = Object.keys(monthlyData).sort();

    return {
        labels: sortedKeys.map(key => {
            // Format as month/year if it looks like a date
            if (key.match(/^\d{4}-\d{2}$/)) {
                return moment(key, 'YYYY-MM').format('MMM/YY');
            }
            return key.length > 15 ? key.substring(0, 15) + '...' : key;
        }),
        fullLabels: sortedKeys.map(key => {
            // Also format date keys for consistency in tooltips, or keep raw? 
            // Better to keep raw name but format date.
            if (key.match(/^\d{4}-\d{2}$/)) {
                return moment(key, 'YYYY-MM').format('MMMM YYYY');
            }
            return key;
        }),
        conformityRates: sortedKeys.map(key => {
            const data = monthlyData[key];
            return data.total > 0 ? (data.valid / data.total) * 100 : 0;
        }),
        nonConformityRates: sortedKeys.map(key => {
            const data = monthlyData[key];
            return data.total > 0 ? (data.invalid / data.total) * 100 : 0;
        })
    };
}

/**
 * Process product inspections data to get top N products by inspection count
 * @param {Array} productData - Raw product inspections data
 * @param {number} limit - Number of top products to return
 * @returns {Array} Top products with { name, total } fields
 */
function processTopProducts(productData, limit = 5) {
    if (!productData || productData.length === 0) {
        return [];
    }

    // Aggregate by product name
    const productTotals = {};

    productData.forEach(item => {
        const name = item.name || 'Desconhecido';
        if (!productTotals[name]) {
            productTotals[name] = 0;
        }
        productTotals[name] += item.qtt || 0;
    });

    // Convert to array, sort by total, and take top N
    return Object.entries(productTotals)
        .map(([name, total]) => ({ name, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, limit);
}

export const DashboardService = {
    buildEmptyStats,
    summary,
    monthlyInspections,
    equipmentStatus,
    employeeInspections,
    inspectionTypeInspections,
    productInspections,
    processConformityTrend,
    processTopProducts
}

